/** Browser-detected IANA zones, never offsets (which change with daylight saving). */
export const CALENDAR_TIME_ZONE_HEADER = "X-InControl-Time-Zone";
// Preserve existing scheduling for accounts that have not opened the app since
// automatic detection was introduced. Never infer an owner's zone from a visitor.
export const LEGACY_CALENDAR_TIME_ZONE = "America/New_York";

export function normalizeCalendarTimeZone(value: unknown): string | null {
  if (typeof value !== "string" || !value.trim() || value.length > 100) return null;
  try {
    return new Intl.DateTimeFormat("en", { timeZone: value.trim() }).resolvedOptions().timeZone;
  } catch {
    return null;
  }
}

export function getCalendarTimeZone(settings?: { calendarTimeZone?: unknown } | null): string {
  return normalizeCalendarTimeZone(settings?.calendarTimeZone) ?? LEGACY_CALENDAR_TIME_ZONE;
}

export function browserCalendarTimeZone(): string | null {
  try {
    return normalizeCalendarTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone);
  } catch {
    return null;
  }
}

/** An instant expressed in the workspace's wall-clock calendar, never the viewer's. */
export function calendarLocalParts(now: Date, timeZone: string = LEGACY_CALENDAR_TIME_ZONE) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone, year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hourCycle: "h23",
  }).formatToParts(now);
  const value = (type: Intl.DateTimeFormatPartTypes) => parts.find(p => p.type === type)!.value;
  return {
    date: `${value("year")}-${value("month")}-${value("day")}`,
    minutes: Number(value("hour")) * 60 + Number(value("minute")),
  };
}

/**
 * Date-only adapter for existing date pickers/local civil-date arithmetic.
 * This is NOT an appointment instant: never serialize it to UTC or apply a
 * second time-zone conversion. Noon avoids browser DST transitions at midnight.
 */
export function calendarCivilDate(now: Date, timeZone: string = LEGACY_CALENDAR_TIME_ZONE): Date {
  return new Date(`${calendarLocalParts(now, timeZone).date}T12:00:00`);
}

/** Find the next owner-local date boundary, including 23/25-hour DST days. */
export function nextCalendarMidnight(now: Date, timeZone: string): number {
  const date = calendarLocalParts(now, timeZone).date;
  let low = now.getTime();
  let high = low + 48 * 60 * 60 * 1000;
  while (high - low > 1) {
    const middle = Math.floor((low + high) / 2);
    if (calendarLocalParts(new Date(middle), timeZone).date === date) low = middle;
    else high = middle;
  }
  return high;
}
